/** Compressed session residency is independent of the user's persistent disk allowance. */
export class AudioFetchError extends Error {
  readonly url: string;
  readonly transient: boolean;
  readonly offline: boolean;
  constructor(url: string, status?: number, offline = false) {
    const transient = status === undefined || status === 408 || status === 429 || (status >= 500 && status < 600);
    super(transient ? `waiting for network: ${url}`
      : `Audio asset unavailable (${status}): ${url}. This release needs repair.`);
    this.url = url;
    this.transient = transient;
    this.offline = offline;
  }
}

/** ±20% jitter avoids synchronized clients; even jittered retries never exceed 30 s. */
export function retryDelayMs(retry: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.min(retry, 5) * (0.8 + Math.random() * 0.4));
}

export type DownloadEvent =
  | { type: 'session_download'; parts: number; bytes: number; seconds: number; retries: number }
  | { type: 'download_hidden'; parts: number; readyParts: number; missingParts: number;
      missingBytes: number };

interface Entry {
  url: string;
  expectedBytes: number;
  promise: Promise<ArrayBuffer>;
  resolve: (bytes: ArrayBuffer) => void;
  reject: (error: unknown) => void;
  bytes?: ArrayBuffer;
  waiting: boolean;
  waiters: Set<(url: string) => void>;
}

// DECISIONS 2.7 measures ~6 MB of Opus for an hour. 64 MiB leaves >10x headroom
// without allowing a corrupt manifest or an unexpectedly huge sit to exhaust a phone.
const MAX_SESSION_BYTES = 64 * 1024 * 1024;

export class SessionDownload {
  private readonly entries = new Map<string, Entry>();
  private readonly abort = new AbortController();
  private readonly wakes = new Set<() => void>();
  private wakeVersion = 0;
  private totalBytes = 0;
  private readyParts = 0;
  private retries = 0;
  private hiddenReported = false;
  private readonly startedAt = Date.now();
  private readonly emit: (event: DownloadEvent) => void;
  private readonly maxBytes: number;

  constructor(
    assets: { url: string; bytes: number }[],
    fetchBytes: (url: string, signal: AbortSignal) => Promise<ArrayBuffer>,
    emit: (event: DownloadEvent) => void,
    maxBytes = MAX_SESSION_BYTES,
  ) {
    this.emit = emit;
    this.maxBytes = maxBytes;
    for (const asset of assets) {
      let resolve!: Entry['resolve'];
      let reject!: Entry['reject'];
      const promise = new Promise<ArrayBuffer>((yes, no) => { resolve = yes; reject = no; });
      // Prefetch failures are retained for the eventual reader, even if it has not arrived.
      void promise.catch(() => undefined);
      this.entries.set(asset.url, { url: asset.url, expectedBytes: asset.bytes,
        promise, resolve, reject, waiting: false, waiters: new Set() });
    }
    window.addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.visibility);
    const queue = [...this.entries.values()];
    const worker = async () => {
      while (!this.abort.signal.aborted) {
        const entry = queue.shift();
        if (!entry) return;
        await this.download(entry, fetchBytes);
      }
    };
    // Queue order is schedule order; only compressed bytes, never decoded buffers, live here.
    void Promise.all([worker(), worker()]).then(() => {
      if (!this.abort.signal.aborted && this.readyParts === this.entries.size) {
        this.emit({ type: 'session_download', parts: this.readyParts, bytes: this.totalBytes,
          seconds: (Date.now() - this.startedAt) / 1000, retries: this.retries });
      }
    });
    if (document.visibilityState !== 'visible') this.visibility();
  }

  has(url: string): boolean { return this.entries.has(url); }

  async read(url: string, onWaiting?: (url: string) => void): Promise<ArrayBuffer> {
    const entry = this.entries.get(url);
    if (!entry) throw new Error(`Audio asset is outside the session: ${url}`);
    if (entry.bytes) return entry.bytes;
    if (onWaiting) {
      entry.waiters.add(onWaiting);
      if (entry.waiting) onWaiting(url);
    }
    try { return await entry.promise; }
    finally { if (onWaiting) entry.waiters.delete(onWaiting); }
  }

  private async download(entry: Entry,
    fetchBytes: (url: string, signal: AbortSignal) => Promise<ArrayBuffer>): Promise<void> {
    let retry = 0;
    while (!this.abort.signal.aborted) {
      const version = this.wakeVersion;
      try {
        const bytes = await fetchBytes(entry.url, this.abort.signal);
        if (this.abort.signal.aborted) return;
        if (this.totalBytes + bytes.byteLength > this.maxBytes) {
          throw new Error(`Session audio exceeds the compressed memory limit: ${entry.url}`);
        }
        this.totalBytes += bytes.byteLength;
        this.readyParts++;
        entry.bytes = bytes;
        entry.waiting = false;
        entry.resolve(bytes);
        return;
      } catch (error) {
        if (this.abort.signal.aborted) return;
        if (!(error instanceof AudioFetchError) || !error.transient) {
          entry.reject(error);
          return;
        }
        entry.waiting = true;
        for (const notify of entry.waiters) notify(entry.url);
        if (version === this.wakeVersion) await this.delay(retryDelayMs(retry++));
        if (!this.abort.signal.aborted) this.retries++;
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const wake = () => {
        clearTimeout(timer);
        this.wakes.delete(wake);
        resolve();
      };
      const timer = setTimeout(wake, ms);
      this.wakes.add(wake);
    });
  }

  private wake = (): void => {
    this.wakeVersion++;
    for (const wake of [...this.wakes]) wake();
  };

  private visibility = (): void => {
    if (document.visibilityState === 'visible') { this.wake(); return; }
    if (this.hiddenReported || this.readyParts === this.entries.size) return;
    this.hiddenReported = true;
    this.emit({ type: 'download_hidden', parts: this.entries.size, readyParts: this.readyParts,
      missingParts: this.entries.size - this.readyParts,
      missingBytes: [...this.entries.values()].reduce((sum, entry) =>
        sum + (entry.bytes ? 0 : entry.expectedBytes), 0) });
  };

  dispose(): void {
    this.abort.abort();
    this.wake();
    window.removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.visibility);
    for (const entry of this.entries.values()) entry.reject(new DOMException('Session ended', 'AbortError'));
    this.entries.clear();
    this.totalBytes = 0;
  }
}
